import { PostgrestResponse } from '@supabase/supabase-js';
import { ITelefones } from '../../../types/tables/ITelefones';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

/**
 * Serviço para gerenciamento de telefones
 */
export class TelefonesService extends BaseMultiTenantService<ITelefones> {
  constructor() {
    super('telefones', ['numero', 'ddd', 'tipo'], 'fk_id_organizacao')
  }

  /**
   * Busca telefones de uma pessoa específica
   */
  async getByPessoa(organizationId: number, pessoaId: number): Promise<PostgrestResponse<ITelefones[]>> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select('*')
      .eq(this.organizationIdField, organizationId)
      .eq('fk_id_pessoa', pessoaId)
      .is('deleted_at', null)
      .order('principal', { ascending: false })

    if (error) {
      throw new Error(`Erro ao buscar telefones da pessoa: ${error.message}`)
    }

    return { data: data || [], error } as PostgrestResponse<ITelefones[]>
  }

  /**
   * Busca telefone principal de uma pessoa
   */
  async getPrincipalByPessoa(organizationId: number, pessoaId: number): Promise<ITelefones | null> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select('*')
      .eq(this.organizationIdField, organizationId)
      .eq('fk_id_pessoa', pessoaId)
      .eq('principal', true)
      .is('deleted_at', null)
      .single()

    if (error && error.code !== 'PGRST116') { // PGRST116 = No rows found
      throw new Error(`Erro ao buscar telefone principal: ${error.message}`)
    }

    return data || null
  }

  /**
   * Define um telefone como principal (desmarca outros da mesma pessoa)
   */
  async setPrincipal(organizationId: number, pessoaId: number, telefoneId: number): Promise<void> {
    // Primeiro, remove a marcação principal de todos os telefones da pessoa
    const { error: updateError } = await this.client
      .from(this.tableName)
      .update({ principal: false })
      .eq(this.organizationIdField, organizationId)
      .eq('fk_id_pessoa', pessoaId)

    if (updateError) {
      throw new Error(`Erro ao remover principal anterior: ${updateError.message}`)
    }

    // Depois, define o telefone específico como principal
    const { error: setPrincipalError } = await this.client
      .from(this.tableName)
      .update({ principal: true })
      .eq('id', telefoneId)
      .eq(this.organizationIdField, organizationId)

    if (setPrincipalError) {
      throw new Error(`Erro ao definir telefone principal: ${setPrincipalError.message}`)
    }
  }
}

export const telefonesService = new TelefonesService()
